{
  "nav": {
    "linkLabel": "Platform administration",
    "dashboard": "Dashboard",
    "users": "Users",
    "people": "Competitions",
    "judges": "Judge Governance",
    "admins": "Platform Administrators",
    "erasureRequests": "Account erasure requests",
    "audit": "Audit Log",
    "competitionRequests": "Competition requests",
    "discoveryCategories": "Discovery categories",
    "adminGuide": "Admin guide",
    "toggleMenu": "Menu",
    "groups": {
      "overview": "Overview",
      "peopleAndAccess": "People & access",
      "competitionGovernance": "Competition governance",
      "securityAndAudit": "Security & audit"
    }
  },
  "erasureRequests": {
    "title": "Account erasure requests",
    "description": "Review account erasure (GDPR-style PII anonymization) requests. Approving one executes it immediately: the account's identifying details (name, email, etc.) are anonymized and its login credentials are permanently deleted; historical competition records (submissions, scores, results, etc.) are kept but anonymized, to preserve the integrity of competition results. This cannot be undone.",
    "listTitle": "Requests",
    "filterStatus": "Filter by status",
    "empty": "No requests match the current filter.",
    "noReason": "(no reason given)",
    "columns": {
      "user": "User",
      "status": "Status",
      "requestedAt": "Requested",
      "reason": "Reason",
      "actions": "Actions"
    },
    "status": {
      "pending": "Pending",
      "approved": "Approved & executed",
      "rejected": "Rejected",
      "cancelled": "Cancelled",
      "all": "All"
    },
    "actions": {
      "approve": "Approve & execute erasure",
      "reject": "Reject"
    },
    "approveTitle": "Approve and immediately execute account erasure?",
    "approveImpact": "This account's identifying details (name, email, etc.) will be anonymized immediately and its login credentials permanently deleted — this cannot be undone. Historical competition records are kept but anonymized.",
    "approveSuccess": "Account erasure approved and executed.",
    "rejectTitle": "Reject this erasure request?",
    "rejectImpact": "The account is unaffected and can continue to sign in normally. The user may submit another request later.",
    "rejectSuccess": "Request rejected."
  },
  "discoveryCategories": {
    "title": "Discovery category management",
    "description": "The platform-wide taxonomy (e.g. AI, Entrepreneurship) used to filter the public discovery list. Completely separate from a competition's own tracks (preliminary/final groups) — they never affect each other.",
    "nameLabel": "Name",
    "slugLabel": "Slug",
    "create": "Add category",
    "createError": "Could not create the category.",
    "loading": "Loading categories…",
    "empty": "No categories yet.",
    "active": "Active",
    "inactive": "Inactive",
    "activate": "Activate",
    "deactivate": "Deactivate"
  },
  "layout": {
    "loading": "Loading…",
    "title": "Platform administration",
    "description": "Cross-competition governance: users, roles, judges and audit history for the whole platform.",
    "navLabel": "Platform administration sections"
  },
  "dialog": {
    "targetLabel": "Target",
    "scopeLabel": "Scope",
    "impactLabel": "Expected impact",
    "reasonLabel": "Reason (required)",
    "genericError": "The action failed. Nothing was changed.",
    "working": "Working…",
    "scopePlatformWide": "Platform-wide"
  },
  "table": {
    "loading": "Loading…",
    "range": "{{from}}–{{to}} of {{total}}",
    "previous": "Previous",
    "next": "Next",
    "exportTsv": "Export TSV",
    "exportExcel": "Export Excel"
  },
  "dashboard": {
    "loading": "Loading dashboard…",
    "loadError": "Could not load the platform dashboard.",
    "unavailable": "Unavailable",
    "degraded": {
      "badge": "Degraded",
      "description": "Some metrics could not be computed this time: {{metrics}}. The rest of the dashboard is still accurate."
    },
    "actionNeeded": {
      "title": "Action needed",
      "description": "Open items and governance anomalies detected right now.",
      "empty": "Nothing needs your attention right now.",
      "pendingCompetitionRequests": "{{count}} competition request(s) awaiting review",
      "lastActiveSuperAdmin": "Only 1 active super admin — consider adding a backup",
      "judgeConflicts": "Judge assignment conflicts detected — needs manual review"
    },
    "groups": {
      "scale": "Platform scale",
      "governance": "Governance status",
      "health": "Operational health"
    },
    "metrics": {
      "activeUsers": "Active users",
      "suspendedUsers": "Suspended users",
      "totalCompetitions": "Total competitions",
      "activeCompetitions": "Active competitions",
      "judgeRelationshipCount": "Judge relationships",
      "platformAdministratorCount": "Active super admins",
      "pendingCompetitionRequests": "Pending requests",
      "competitionComposition": "{{active}} active · {{other}} ended/draft",
      "registeredTeamCount": "Registered teams",
      "judgingCompletionRate": "Judging completion rate"
    },
    "recentAudit": {
      "title": "Recent important governance activity",
      "description": "The latest important governance actions across every competition. Field-level form edits are hidden by default.",
      "empty": "No recent governance activity.",
      "emptyFiltered": "No important governance activity right now; switch to show all activity.",
      "systemActor": "System",
      "viewAll": "View full audit log →",
      "showAll": "Show all activity",
      "showImportantOnly": "Show important events only",
      "categories": {
        "identity": "Identity",
        "form": "Form",
        "judging": "Judging",
        "role": "Role",
        "competition": "Competition"
      }
    },
    "quickLinks": {
      "title": "Quick governance links",
      "users": "View users",
      "competitionRequests": "Review competition requests",
      "judges": "View judge governance",
      "audit": "View audit log"
    }
  },
  "users": {
    "title": "Users",
    "description": "Every account on the platform, with its account status, global role and competition roles.",
    "loadError": "Could not load users.",
    "empty": "No users match these filters.",
    "filters": {
      "title": "Filters",
      "search": "Search",
      "searchPlaceholder": "Name or email",
      "status": "Account status",
      "role": "Competition role",
      "any": "Any",
      "judgeOnly": "Judges only",
      "judgeOnlyHint": "Only show users with an active judge role"
    },
    "columns": {
      "displayName": "Display name",
      "email": "Email",
      "accountStatus": "Account status",
      "globalRoleSummary": "Global role",
      "competitionCount": "Competitions",
      "competitionRoleSummary": "Competition roles",
      "judgeRelationshipCount": "Judge relationships",
      "createdAt": "Created at",
      "lastActivityAt": "Last activity"
    }
  },
  "userDetail": {
    "loading": "Loading…",
    "loadError": "Could not load this user.",
    "superAdminBadge": "Super admin",
    "tabsLabel": "User detail sections",
    "tabs": {
      "basic": "Basic info",
      "competitions": "Competitions & roles",
      "teams": "Teams",
      "judges": "Judging",
      "security": "Account & security",
      "audit": "Audit history"
    },
    "basic": {
      "id": "User ID",
      "displayName": "Display name",
      "email": "Email",
      "accountStatus": "Account status",
      "globalRoleSummary": "Global role",
      "authMethodSummary": "Authentication methods",
      "createdAt": "Created at",
      "lastActivityAt": "Last activity"
    },
    "competitions": {
      "empty": "This user holds no competition role.",
      "grantedAt": "Granted at {{date}}",
      "revokeRole": "Revoke {{role}}",
      "revokeRoleTitle": "Revoke competition role",
      "revokeRoleImpact": "The user will lose the \"{{role}}\" role in this competition. Any existing team/judge relationship record is not deleted by this action.",
      "revokeSubmit": "Revoke role",
      "revokeSuccess": "The role was revoked.",
      "grantCompetitionSearch": "Search competition name",
      "grantCompetitionSearchPlaceholder": "Type a competition name…",
      "grantRole": "Role",
      "grantSubmit": "Grant role",
      "grantRoleTitle": "Grant competition role",
      "grantRoleImpact": "The user will be granted the \"{{role}}\" role in this competition.",
      "grantSuccess": "The role was granted."
    },
    "teams": {
      "empty": "This user has no team relationship on any competition."
    },
    "judges": {
      "empty": "This user has no judge relationship on any competition.",
      "summary": "{{submitted}}/{{assignments}} scores submitted, {{locked}} locked, {{conflicts}} conflicts"
    },
    "security": {
      "suspend": "Suspend user",
      "reactivate": "Reactivate user",
      "revokeSessions": "Revoke all sessions",
      "grantAdmin": "Grant platform administrator",
      "revokeAdmin": "Revoke platform administrator",
      "noPermission": "You do not hold permission to manage this user's account."
    },
    "dialogs": {
      "suspendImpact": "This account will be suspended: every active session is revoked immediately, and the account cannot sign in until reactivated. No submission, score, result or audit history is deleted.",
      "suspendSuccess": "The user was suspended and their sessions were revoked.",
      "reactivateImpact": "This account will be reactivated and may sign in again.",
      "reactivateSuccess": "The user was reactivated.",
      "revokeSessionsImpact": "Every currently active session for this user is revoked immediately. The account itself is not suspended.",
      "revokeSessionsSuccess": "All sessions for this user were revoked.",
      "grantAdminImpact": "This account will gain every platform administration permission, platform-wide, effective immediately on its next request.",
      "grantAdminSuccess": "Platform administrator access was granted.",
      "revokeAdminImpact": "This account will lose every platform administration permission, effective immediately on its next request. This is refused by the server if this is the platform's last active super admin.",
      "revokeAdminSuccess": "Platform administrator access was revoked."
    },
    "audit": {
      "empty": "No audit entries reference this user.",
      "reason": "Reason: {{reason}}"
    }
  },
  "judges": {
    "title": "Judges",
    "description": "One row per user × competition judge relationship; the same person judging two competitions appears twice.",
    "loadError": "Could not load judges.",
    "empty": "No judge relationships match these filters.",
    "profileSet": "Profile set",
    "profileNotSet": "Profile not set",
    "filters": {
      "title": "Filters",
      "search": "Search",
      "searchPlaceholder": "Name or email",
      "competition": "Competition",
      "competitionPlaceholder": "Search by competition name"
    },
    "columns": {
      "displayName": "Display name",
      "email": "Email",
      "competition": "Competition",
      "profileStatus": "Profile status",
      "assignmentCount": "Assignments",
      "submittedScoreCount": "Submitted",
      "lockedScoreCount": "Locked",
      "conflictCount": "Conflicts",
      "completionSummary": "Completion"
    }
  },
  "people": {
    "title": "Competition people",
    "description": "Everyone with a role in this competition, filterable by role.",
    "loadError": "Could not load people for this competition.",
    "empty": "No people match these filters.",
    "backToPicker": "← Choose a different competition",
    "judgeSummary": "{{submitted}}/{{assignments}} scores submitted",
    "picker": {
      "title": "Choose a competition",
      "description": "Pick a competition to view and filter its people by role.",
      "search": "Search",
      "searchPlaceholder": "Competition name or slug",
      "empty": "No competitions match this search.",
      "loadError": "Could not load competitions.",
      "viewPeople": "View people →"
    },
    "list": {
      "title": "Competitions",
      "description": "View every competition on the platform, its status, and governance info.",
      "summary": "{{total}} competitions · {{active}} active · {{draft}} draft · {{ended}} ended",
      "filters": {
        "title": "Filters",
        "status": "Status",
        "year": "Year",
        "any": "Any"
      },
      "columns": {
        "name": "Competition",
        "slug": "Slug",
        "status": "Status",
        "actions": "Actions"
      },
      "viewAction": "View"
    },
    "filters": {
      "title": "Filters",
      "search": "Search",
      "searchPlaceholder": "Name or email",
      "role": "Role",
      "any": "Any"
    },
    "columns": {
      "displayName": "Display name",
      "email": "Email",
      "accountStatus": "Account status",
      "roles": "Roles",
      "team": "Team",
      "judge": "Judge summary",
      "grantedAt": "Granted at"
    }
  },
  "admins": {
    "title": "Platform administrators",
    "description": "Every account currently holding platform-wide super admin access.",
    "loadError": "Could not load platform administrators.",
    "empty": "No platform administrators match this search.",
    "lastActiveAdmin": "Last active admin (cannot revoke)",
    "filters": {
      "title": "Filters",
      "search": "Search",
      "searchPlaceholder": "Name or email"
    },
    "actions": {
      "menu": "Actions for {{name}}",
      "viewUser": "View user",
      "viewAudit": "View related audit"
    },
    "columns": {
      "displayName": "Display name",
      "email": "Email",
      "accountStatus": "Account status",
      "grantedAt": "Granted at",
      "grantedBy": "Granted by",
      "recentAudit": "Recent audit",
      "actions": "Actions"
    }
  },
  "audit": {
    "title": "Platform audit",
    "description": "Every platform governance action, across every competition. Read-only.",
    "loadError": "Could not load the audit log.",
    "empty": "No audit entries match these filters.",
    "search": {
      "label": "Search this page",
      "placeholder": "Name, email, action, or entity"
    },
    "filters": {
      "title": "Filters",
      "actorId": "Actor user ID",
      "targetUserId": "Target user ID",
      "competitionId": "Competition ID",
      "action": "Action",
      "from": "From",
      "to": "To"
    },
    "columns": {
      "actor": "Actor",
      "action": "Action",
      "competition": "Competition",
      "entityType": "Entity type",
      "reason": "Reason",
      "createdAt": "Time"
    },
    "actions": {
      "generic": "performed \"{{action}}\"",
      "competitionCreate": "created the competition",
      "competitionUpdate": "updated the competition",
      "competitionDeadlineChange": "changed a competition deadline",
      "competitionStatusChange": "changed the competition status",
      "competitionSettingsUpdate": "updated competition settings",
      "categoryCreate": "created a category",
      "categoryUpdate": "updated a category",
      "categoryArchive": "archived a category",
      "roundCreate": "created a round",
      "roundUpdate": "updated a round",
      "roundAdvancement": "ran round advancement",
      "roundManualFieldCreate": "added a round manual input field",
      "roundManualFieldUpdate": "changed a round manual input field",
      "roundManualFieldDelete": "deleted a round manual input field",
      "manualScoreEnter": "entered or changed a staff score",
      "formCreate": "created a form",
      "formUpdate": "updated a form",
      "formArchive": "archived a form",
      "formFieldCreate": "added a form field",
      "formFieldUpdate": "updated a form field",
      "formFieldDelete": "deleted a form field",
      "formFieldReorder": "reordered form fields",
      "formVersionClone": "cloned a form version",
      "formVersionPublish": "published a form version",
      "roleGrant": "granted a competition role",
      "roleRevoke": "revoked a competition role",
      "teamChange": "changed team details",
      "teamMemberRemove": "removed a team member",
      "teamLeaderTransfer": "transferred team leadership",
      "teamInviteCreate": "created a team invite",
      "teamInviteResend": "resent a team invite",
      "teamInviteRevoke": "revoked a team invite",
      "teamInviteAccept": "accepted a team invite",
      "teamRegistrationReminderSend": "sent a registration reminder",
      "registrationNeedsUpdateReminderSend": "sent a registration update reminder",
      "registrationNeedsUpdateReminderTestSend": "sent themself a test registration update reminder",
      "submissionCreate": "created a submission",
      "submissionUpdate": "updated a submission",
      "submissionSubmit": "submitted a submission",
      "submissionLock": "locked a submission",
      "submissionUnlock": "unlocked a submission",
      "submissionWithdraw": "withdrew a submission",
      "submissionDisqualify": "disqualified a submission",
      "submissionReinstate": "reinstated a disqualified submission",
      "submissionReturnForCorrection": "returned a submission for correction",
      "fileUploadComplete": "completed a file upload",
      "fileUploadFailed": "reported a file upload as failed",
      "fileDelete": "deleted a file",
      "judgeInvitationDecline": "declined a judge invitation",
      "judgeAutoReminderUpdate": "changed the automatic judge reminder",
      "judgeWithdrawal": "withdrew from judging",
      "judgeAcknowledgement": "acknowledged the judging terms",
      "judgeReminderSend": "sent a judging reminder",
      "judgeAssignment": "assigned a judge",
      "judgeReassignment": "reassigned a judge",
      "scoreSubmit": "submitted a score",
      "scoreReopen": "reopened a score",
      "scoreChange": "changed a score",
      "resultCalculate": "calculated results",
      "resultLock": "locked results",
      "resultPublish": "published results",
      "resultOverride": "overrode a result",
      "announcementPublish": "published an announcement",
      "announcementUpdate": "edited an announcement",
      "announcementDelete": "deleted an announcement",
      "announcementEmailSend": "emailed an announcement",
      "announcementEmailTestSend": "sent themself a test announcement email",
      "notificationEmailDelivery": "the system sent notification emails (delivery result)",
      "authLogin": "signed in",
      "authLogout": "signed out",
      "authLoginLocked": "was locked out after too many failed sign-ins",
      "authRegister": "registered an account",
      "authPasswordReset": "reset a password",
      "initialSuperAdminBootstrap": "completed initial super admin setup",
      "userSuspend": "suspended a user",
      "userReactivate": "reactivated a user",
      "userSessionsRevoke": "revoked a user's sessions",
      "superAdminGrant": "granted super admin",
      "superAdminRevoke": "revoked super admin",
      "judgeInvitationCreate": "created a judge invitation",
      "judgeInvitationCancel": "cancelled a judge invitation",
      "judgeInvitationResend": "resent a judge invitation",
      "authForcedPasswordChangeComplete": "completed the forced first-login password change",
      "competitionRequestSubmit": "submitted a competition request",
      "competitionRequestCancel": "cancelled a competition request",
      "competitionRequestRequestChanges": "requested changes",
      "competitionRequestReject": "rejected a competition request",
      "competitionRequestApprove": "approved a competition request",
      "profileSelfUpdate": "updated their own profile",
      "authPasswordChange": "changed their own password",
      "authEmailChangeRequested": "requested an email change",
      "authEmailChangeVerified": "changed their own email",
      "sessionSelfRevoke": "revoked their own session",
      "platformDiscoveryCategoryCreate": "created a discovery category",
      "platformDiscoveryCategoryUpdate": "updated a discovery category",
      "eligibilityPolicyUpdate": "updated the student eligibility policy",
      "eligibilityProofUploadComplete": "uploaded an enrollment proof",
      "eligibilityProofReplace": "replaced an enrollment proof",
      "eligibilityVerificationApproved": "verified a student's eligibility",
      "eligibilityVerificationRejected": "rejected a student's eligibility",
      "eligibilityProofRetentionDelete": "system automatically deleted an expired enrollment proof file",
      "accountErasureRequested": "requested account erasure",
      "accountErasureCancelled": "cancelled an account erasure request",
      "accountErasureRejected": "rejected an account erasure request",
      "accountErasureExecuted": "executed account erasure (PII anonymized)",
      "dataExportDownloaded": "exported personal data",
      "adminReportExport": "exported a competition report",
      "competitionConfigExport": "exported the competition configuration",
      "competitionClone": "cloned a competition",
      "templateCreate": "created a competition template",
      "templateRevisionCreate": "updated a competition template",
      "templateCreateCompetition": "created a competition from a template",
      "configImportCreate": "imported a competition from JSON"
    }
  },
  "competitionRequests": {
    "title": "Competition requests",
    "description": "Review requests from regular users to host a new competition. Approving one automatically creates the competition and grants the applicant its admin role — no separate manual step is needed.",
    "loadError": "Could not load requests.",
    "empty": "No requests match these filters.",
    "filters": {
      "title": "Filters",
      "status": "Status",
      "any": "Any"
    },
    "tabs": {
      "all": "All"
    },
    "columns": {
      "proposedName": "Proposed competition name",
      "organization": "Organization",
      "contactPerson": "Contact person",
      "status": "Status",
      "submittedAt": "Submitted at",
      "actions": "Actions"
    },
    "detail": {
      "loading": "Loading…",
      "loadError": "Could not load this request.",
      "backToList": "← Back to competition requests",
      "applicantSection": "Application",
      "governanceSection": "Governance context",
      "reviewSection": "Review history",
      "fields": {
        "proposedName": "Proposed competition name",
        "proposedSlug": "Proposed URL slug",
        "organization": "Organizer / organization",
        "contactPerson": "Contact person",
        "contactEmail": "Contact email",
        "description": "Purpose & description",
        "timezone": "Timezone",
        "expectedStartAt": "Expected start",
        "expectedEndAt": "Expected end",
        "expectedParticipants": "Expected participants",
        "judgingRequired": "Judging required",
        "twoRoundRequired": "Two-round competition",
        "studentMinorData": "Involves student / minor data",
        "notes": "Notes",
        "submittedAt": "Submitted at",
        "reviewerUserId": "Reviewer ID",
        "reviewedAt": "Reviewed at",
        "reviewReason": "Review reason",
        "createdCompetitionId": "Created competition ID"
      },
      "yes": "Yes",
      "no": "No",
      "none": "None"
    },
    "actions": {
      "review": "Review",
      "view": "View",
      "viewResult": "View result",
      "startReview": "Start review",
      "startReviewSuccess": "Review started for this request.",
      "requestChanges": "Request changes",
      "requestChangesImpact": "The applicant will see your reason and may submit a new request. This request itself is neither approved nor rejected.",
      "requestChangesSuccess": "Changes were requested from the applicant.",
      "reject": "Reject request",
      "rejectImpact": "This request will be marked rejected and no competition will be created. The applicant can see the rejection reason.",
      "rejectSuccess": "The request was rejected.",
      "approve": "Approve and create competition",
      "approveImpact": "Approving immediately creates the real competition with baseline settings, and automatically grants the applicant that competition's first admin role — no manual competition creation or admin assignment is needed afterward.",
      "approveSuccess": "The request was approved and the competition was created.",
      "alreadyProvisioned": "This request was already approved and provisioned earlier — nothing was created again.",
      "viewCompetition": "View the created competition →",
      "noPermission": "You do not hold permission to review competition requests."
    }
  }
}
